import React, { useCallback, useEffect, useRef, useState } from "react";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { chunkUpload } from "./EnginePanel";
import { PunchCompare } from "./PunchCompare";
import {
  AlertTriangle, Building2, CalendarClock, CalendarPlus, ClipboardCheck, Copy, FileCheck2, Flag, Inbox, LayoutTemplate,
  Link2, Loader2, Mail, MessageSquareText, Package, Pencil, PenLine, Plus, Ruler, Send, Settings2, Trash2, Upload,
} from "lucide-react";
import { fmtD, fmtMD } from "../lib/date";

import { T, TB } from "../lib/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MANAGE = ["admin", "ceo", "eng_manager", "pm"];
const INSP_CHIP = {
  scheduled: "bg-blue-100 text-blue-700", passed: "bg-emerald-100 text-emerald-700",
  rectifying: "bg-red-100 text-red-700", rectified: "bg-amber-100 text-amber-700",
};
const MAT_CHIP = {
  pending: "bg-amber-100 text-amber-700", ordered: "bg-blue-100 text-blue-700",
  received: "bg-emerald-100 text-emerald-700", skipped: "bg-slate-100 text-slate-400",
};
const COMM_CHIP = {
  draft: "bg-slate-100 text-slate-500", sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700", logged: "bg-blue-100 text-blue-700",
};
const IB_KIND = {
  done: ["完成汇报", "Done", "bg-emerald-100 text-emerald-700"], confirm: ["确认", "Confirm", "bg-blue-100 text-blue-700"],
  blocker: ["阻塞/推迟", "Blocked", "bg-red-100 text-red-700"], question: ["疑问", "Question", "bg-amber-100 text-amber-700"],
  unknown: ["未识别", "Unknown", "bg-slate-100 text-slate-500"],
};
const IB_STATUS = {
  auto_applied: ["自动登记", "Auto-applied", "bg-emerald-600 text-white"], pending: ["待处理", "Pending", "bg-amber-100 text-amber-700"],
  applied: ["已登记", "Applied", "bg-emerald-100 text-emerald-700"], ignored: ["已忽略", "Ignored", "bg-slate-100 text-slate-400"],
  unmatched: ["未匹配", "Unmatched", "bg-red-100 text-red-700"],
};
const META_EN = {
  "Drywall 完成后": "After drywall", "Drywall 开工": "Drywall start", "Finishes 开工": "Finishes start",
  "Finishes 开工/材料就绪复核": "Finishes start / readiness check", "Foundation 开工": "Foundation start",
  "Framing 开工": "Framing start", "Jen 准备": "Jen prepares", "Permit pulled 后立即": "Right after permit pulled",
  "淋浴瓷砖完成+现场测量后": "After shower tile + site measure", "热水器安装后": "After water heater install",
  "瓷砖/浴柜现场测量后": "After tile/vanity site measure", "Handyman 自提": "Handyman pickup",
  "Handyman 自提/ODBO 配送": "Handyman pickup / ODBO delivery", "Jen 现场签收": "Jen signs onsite",
  "ODBO 配送": "ODBO delivery", "PM 现场签收": "PM signs onsite", "配送": "Delivery", "配送/自提": "Delivery / pickup",
  "开工前": "Before start", "现场": "Onsite",
};
const MAT_EN = { pending: "Pending", ordered: "Ordered", received: "Received", skipped: "Skipped" };
const INSP_EN = { scheduled: "Scheduled", passed: "Passed", rectifying: "Rectifying", rectified: "Rectified" };
const COMM_EN = { draft: "Draft", sent: "Sent", failed: "Failed", logged: "Logged" };
const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * 项目业务操作面板：接收项目、权限和刷新上下文，返回巡检、采购、通信等业务卡片。
 * 工作台已移除八个分类按钮，因此直接展示所有卡片和异常监控，不再依赖外部分类开关；
 * 保留各卡片原有权限与操作逻辑，并继续用 highlight 定位任务关联的通信记录。
 */
export const EngineOpsPanel = ({ api, id, zh, user, onChanged, refreshTick, highlight }) => {
  const [ops, setOps] = useState(null);
  const [gcal, setGcal] = useState(null);
  const fileRef = useRef(null);
  const fileCtx = useRef(null);
  const [fileBusy, setFileBusy] = useState(false);
  // 【2026-10-26 权限体系改造第三批】面板内全部管理按钮改走可配置动作点 /board::ops-manage：
  // permOf 返回管理员在「角色权限矩阵 / 账号菜单权限」里的最终判定，undefined（权限快照未加载）
  // 时回落改造前的硬编码 MANAGE 名单 —— 首屏与加载失败时行为与改造前完全一致。
  const { permOf } = useApp();
  const canManage = permOf("/board::ops-manage") ?? MANAGE.includes(user?.role);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/projects/${id}/engine/ops`); setOps(d); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick > 0) load(); }, [refreshTick, load]);
  useEffect(() => {
    if (!canManage) return;
    api.get("/gcal/status").then(({ data }) => setGcal(data)).catch(() => {});
  }, [api, canManage]);
  useEffect(() => { if (refreshTick > 0) load(); }, [refreshTick, load]);

  const run = async (fn, okMsg) => {
    try {
      const { data: r } = await fn();
      if (okMsg) toast.success(okMsg);
      if (r?.advanced) toast.success(zh ? `🚀 阶段门槛全部满足，已自动推进至「${r.advanced.toName}」` : "Advanced", { duration: 6000 });
      if (r?.info) toast.info(r.info, { duration: 5000 });
      await load();
      onChanged && onChanged();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); return false; }
  };

  const pickFile = (kind, rid) => { fileCtx.current = { kind, rid }; fileRef.current?.click(); };
  const onFile = async (f) => {
    const ctx = fileCtx.current;
    fileCtx.current = null;
    if (!ctx || !f) return;
    setFileBusy(true);
    try {
      const up = await chunkUpload(api, f);
      if (ctx.kind === "pass") await run(() => api.post(`/projects/${id}/engine/inspections/${ctx.rid}/result`, { passed: true, jobCardFileId: up.fileId, filename: up.filename, contentType: up.contentType }), zh ? "市检通过 ✓ Job card 已归档, 关联任务已完成" : "Passed");
      if (ctx.kind === "rectify") await run(() => api.post(`/projects/${id}/engine/inspections/${ctx.rid}/rectify-clear`, { fileId: up.fileId, filename: up.filename }), zh ? "整改证据已归档 · risk cleared" : "Cleared");
      if (ctx.kind === "material") await run(() => api.post(`/projects/${id}/engine/materials/${ctx.rid}/order`, { fileId: up.fileId, filename: up.filename, contentType: up.contentType }), zh ? "已下单, 证据已留档 ✓" : "Ordered");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setFileBusy(false);
  };

  if (!ops) return <div className="bg-white rounded-xl border border-slate-200 p-6 flex justify-center" data-testid="engine-ops-loading"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;

  return (
    <div data-testid="engine-ops-panel" data-collapsed="0">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <ExceptionsCard ops={ops} api={api} id={id} zh={zh} run={run} canManage={canManage} />
      <InspectionsCard ops={ops} api={api} id={id} zh={zh} run={run} pickFile={pickFile} fileBusy={fileBusy} />
      <MaterialsCard ops={ops} api={api} id={id} zh={zh} run={run} pickFile={pickFile} fileBusy={fileBusy} canManage={canManage} gcal={gcal} setGcal={setGcal} />
      <CommsCard ops={ops} api={api} id={id} zh={zh} run={run} canManage={canManage} userRole={user?.role} highlight={highlight} />
      <InboundCard ops={ops} api={api} id={id} zh={zh} run={run} canManage={canManage} />
      <TaskLinksCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} />
      <PunchCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} onChanged={onChanged} />
      <OwnerConfirmCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} />
      <SignoffCard ops={ops} zh={zh} />
      <PreConCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} />
      <LayoutCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} />
      <CloseoutCard api={api} id={id} zh={zh} canManage={canManage} refreshTick={refreshTick} />
      <input ref={fileRef} type="file" className="hidden" data-testid="ops-file-input"
        onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

function ExceptionsCard({ ops, api, id, zh, run, canManage }) {
  const exs = ops.exceptions || [];
  const [scanning, setScanning] = useState(false);
  const scan = async () => {
    setScanning(true);
    let res = null;
    const ok = await run(() => api.post(`/engine/exceptions/scan-run`, { projectId: id }).then((r) => { res = r.data; return r; }));
    setScanning(false);
    if (ok && res) toast.info(zh ? `扫描完成：新增异常 ${res.opened} · 自动解除 ${res.resolved}` : `opened ${res.opened}, resolved ${res.resolved}`);
  };
  return (
    <div className={`rounded-xl border p-4 md:col-span-2 xl:col-span-4 ${exs.length ? "bg-red-50/60 border-red-200" : "bg-white border-slate-200"}`} data-testid="ops-exceptions-card">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
          <AlertTriangle className={`h-4 w-4 ${exs.length ? "text-red-600" : "text-slate-400"}`} />{zh ? "异常分支监控" : "Exceptions"}
          {exs.length > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white" data-testid="ops-exc-count">{exs.length}</span>}
          <span className="text-[9px] font-normal text-slate-400">KB-10 · {zh ? "每日自动扫描" : "daily scan"}</span>
        </p>
        {canManage && <button data-testid="ops-exc-scan-btn" disabled={scanning} onClick={scan}
          className="text-[11px] font-semibold text-indigo-600 hover:underline disabled:opacity-40">{scanning ? (zh ? "扫描中…" : "…") : (zh ? "立即扫描" : "Scan now")}</button>}
      </div>
      {exs.length === 0 ? (
        <p className="text-[11px] text-slate-300">{zh ? "暂无异常 — 引擎按 KB-10 规则每日扫描：阻塞滞留 / 阶段滞后 / 材料延误 / 市检整改滞留 / 外部回复超时 / 收件阻碍未处理" : "None"}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {exs.map((x) => (
            <div key={x.id} className="rounded-lg bg-white border border-red-100 p-2.5" data-testid={`ops-exc-${x.id}`}>
              <div className="flex items-start justify-between gap-1.5">
                <p className="text-[11px] font-bold text-slate-700 leading-snug">{T(x.title)}</p>
                <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${x.severity === "high" ? "bg-red-600 text-white" : "bg-amber-100 text-amber-700"}`}>{x.severity === "high" ? (zh ? "高" : "high") : (zh ? "中" : "med")}</span>
              </div>
              {x.detail && <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{T(x.detail)}</p>}
              <ol className="mt-1 space-y-0.5">
                {(x.playbook || []).map((s, i) => <li key={i} className="text-[10px] text-slate-500 flex gap-1"><span className="font-bold text-red-400">{i + 1}.</span><span>{T(s)}</span></li>)}
              </ol>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[9px] text-slate-300">{T(x.typeZh)} · {fmtMD(x.detectedAt)}</span>
                {canManage && <button data-testid={`ops-exc-resolve-${x.id}`}
                  onClick={() => run(() => api.post(`/engine/exceptions/${x.id}/resolve`, {}), zh ? "已标记解决" : "Resolved")}
                  className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "标记解决" : "Resolve"}</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InboundCard({ ops, api, id, zh, run, canManage }) {
  const [sim, setSim] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [etaPick, setEtaPick] = useState({});
  const etaMats = (ops.materials || []).filter((m) => !["skipped", "received"].includes(m.status));
  const defMat = etaMats.find((m) => m.emailKind) || etaMats[0];
  const inbound = ops.inbound || [];
  const pending = inbound.filter((r) => r.status === "pending").length;
  const simulate = async () => {
    if (!sim?.body?.trim()) { toast.error(zh ? "请输入消息内容" : "Body required"); return; }
    let st = null;
    const ok = await run(() => api.post(`/engine/inbound/simulate`, { ...sim }).then((res) => { st = res.data?.status; return res; }));
    if (ok) {
      const zhS = { auto_applied: T("已自动登记任务完成"), pending: T("已匹配项目, 待处理"), unmatched: T("未匹配到项目, 已通知经理") };
      toast.success(zh ? `模拟收件已解析：${zhS[st] || st}` : `Simulated: ${st}`);
      setSim(null);
    }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-inbound-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Inbox className="h-4 w-4 text-emerald-600" />{zh ? "收件箱" : "Inbound"}
          {pending > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{pending} {zh ? "待处理" : "pending"}</span>}
        </p>
        {canManage && <button data-testid="ops-inbound-sim-btn" onClick={() => setSim(sim ? null : { channel: "sms", from: "", body: "", media: false, ai: true })}
          className="text-[11px] font-semibold text-indigo-600 hover:underline">{zh ? "模拟收件" : "Simulate"}</button>}
      </div>
      {sim && (
        <div className="mb-2 rounded-lg bg-slate-50 p-2 space-y-1.5" data-testid="ops-inbound-sim-form">
          <div className="flex gap-1.5">
            <select value={sim.channel} onChange={(e) => setSim({ ...sim, channel: e.target.value })} className="text-[10px] rounded border border-slate-200 px-1.5 py-1 bg-white" data-testid="ops-inbound-sim-channel">
              <option value="sms">SMS</option><option value="email">Email</option>
            </select>
            <input value={sim.from} onChange={(e) => setSim({ ...sim, from: e.target.value })} placeholder={zh ? "发件人号码/邮箱" : "From"} className="flex-1 text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-inbound-sim-from" />
          </div>
          <input value={sim.body} onChange={(e) => setSim({ ...sim, body: e.target.value })} placeholder={zh ? "消息内容, 如: All done 已完成" : "Body"} className="w-full text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-inbound-sim-body" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={!!sim.media} onChange={(e) => setSim({ ...sim, media: e.target.checked })} className="accent-emerald-600" data-testid="ops-inbound-sim-media" />{zh ? "附带照片(媒体证据)" : "With media"}
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={!!sim.ai} onChange={(e) => setSim({ ...sim, ai: e.target.checked })} className="accent-indigo-600" data-testid="ops-inbound-sim-ai" />{zh ? "AI语义解析" : "AI classify"}
            </label>
          </div>
          <button onClick={simulate} className="w-full text-[10px] font-semibold rounded px-1.5 py-1 bg-emerald-600 text-white" data-testid="ops-inbound-sim-submit">{zh ? "模拟接收并解析" : "Run"}</button>
        </div>
      )}
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {inbound.length === 0 && !sim && <p className="text-[11px] text-slate-300 text-center py-4">{zh ? "暂无收件 — 分包回复的短信/邮件会自动匹配任务；Twilio webhook 指向 /api/public/engine/inbound/sms 即可接真实短信" : "No inbound"}</p>}
        {inbound.map((r) => {
          const [klz, kle, kc] = IB_KIND[r.kind] || IB_KIND.unknown;
          const [slz, sle, sc] = IB_STATUS[r.status] || IB_STATUS.pending;
          const kl = zh ? klz : kle, sl = zh ? slz : sle;
          return (
            <div key={r.id} className="rounded-lg border border-slate-100 p-2" data-testid={`ops-inbound-${r.id}`}>
              <div className="flex items-center justify-between gap-1">
                <p className="text-[10px] font-bold text-slate-600 truncate min-w-0">{r.channel === "sms" ? "SMS" : r.channel === "portal" ? (zh ? "业主门户" : "Portal") : T("邮件")} · {r.from || "-"}</p>
                <span className="flex gap-1 shrink-0">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${kc}`}>{kl}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sc}`}>{sl}</span>
                </span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{T(r.body)}</p>
              {r.aiSummary && <p className="text-[9px] text-violet-500 mt-0.5 truncate" data-testid={`ops-inbound-ai-${r.id}`}>AI: {r.aiSummary}{typeof r.aiConfidence === "number" ? (zh ? `（置信 ${Math.round(r.aiConfidence * 100)}%）` : ` (${Math.round(r.aiConfidence * 100)}% confidence)`) : ""}</p>}
              {r.suggestedTitle && <p className="text-[9px] text-indigo-500 mt-0.5 truncate">{zh ? "建议任务" : "Suggested"}: {r.suggestedTitle}</p>}
              {r.etaDate && r.status === "pending" && canManage && (
                <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`ops-inbound-eta-${r.id}`}>
                  <span className="text-[9px] font-bold text-sky-600">🚚 {zh ? `检测到到货日期 ${fmtD(r.etaDate)}` : `Delivery date detected: ${fmtD(r.etaDate)}`}</span>
                  <select value={etaPick[r.id] || defMat?.id || ""} onChange={(e) => setEtaPick({ ...etaPick, [r.id]: e.target.value })}
                    className="text-[9px] rounded border border-slate-200 px-1 py-0.5 bg-white max-w-[130px]" data-testid={`ops-inbound-eta-mat-${r.id}`}>
                    <option value="">{zh ? "选择材料…" : "Material…"}</option>
                    {etaMats.map((m) => <option key={m.id} value={m.id}>{m.label.slice(0, 24)}</option>)}
                  </select>
                  <button disabled={!(etaPick[r.id] || defMat?.id)}
                    onClick={() => run(() => api.post(`/engine/inbound/${r.id}/confirm-eta`, { materialId: etaPick[r.id] || defMat?.id }),
                      zh ? "ETA 已确认 ✓ 日历与到货前一天提醒已就绪" : "ETA confirmed — calendar & T-1 reminder ready")}
                    className="text-[9px] font-bold rounded px-1.5 py-0.5 bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40" data-testid={`ops-inbound-eta-confirm-${r.id}`}>{zh ? "确认ETA" : "Confirm ETA"}</button>
                </div>
              )}
              {r.etaDate && r.status === "applied" && <p className="text-[9px] text-emerald-600 mt-0.5" data-testid={`ops-inbound-eta-done-${r.id}`}>🚚 ✓ {zh ? `ETA ${fmtD(r.etaDate)} 已确认` : `ETA ${fmtD(r.etaDate)} confirmed`}</p>}
              {r.repliedAt && <p className="text-[9px] text-emerald-600 mt-0.5" data-testid={`ops-inbound-replied-${r.id}`}>✓ {zh ? "已回复" : "Replied"}（{r.replyStatus === "sent" ? (zh ? "已发送" : "sent") : (zh ? "测试留痕" : "logged")}）· {String(r.repliedAt).slice(5, 16).replace("T", " ")}</p>}
              {r.status === "pending" && r.suggestedReply && !r.repliedAt && replyFor?.id !== r.id &&
                <p className="text-[9px] text-indigo-400 mt-0.5 truncate">✏️ {zh ? "AI已拟好回复草稿" : "AI reply drafted"}</p>}
              {r.status === "pending" && (
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  <button data-testid={`ops-inbound-apply-${r.id}`} disabled={!r.suggestedTaskId}
                    title={r.suggestedTaskId ? "" : (zh ? "无建议任务, 请在引擎面板手动完成" : "No suggestion")}
                    onClick={() => run(() => api.post(`/engine/inbound/${r.id}/apply`, {}), zh ? "已登记任务完成" : "Applied")}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">{zh ? "执行登记" : "Apply"}</button>
                  {canManage && <button data-testid={`ops-inbound-reply-btn-${r.id}`}
                    onClick={() => setReplyFor(replyFor?.id === r.id ? null : { id: r.id, text: r.suggestedReply || "" })}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-600 text-white hover:bg-indigo-700">{zh ? "回复" : "Reply"}</button>}
                  <button data-testid={`ops-inbound-ignore-${r.id}`} onClick={() => run(() => api.post(`/engine/inbound/${r.id}/ignore`), zh ? "已忽略" : "Ignored")}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "忽略" : "Ignore"}</button>
                </div>
              )}
              {r.status === "pending" && replyFor?.id === r.id && (
                <div className="mt-1.5 rounded-lg bg-indigo-50/70 p-1.5 space-y-1" data-testid={`ops-inbound-reply-box-${r.id}`}>
                  <p className="text-[9px] font-bold text-indigo-500">{r.suggestedReply ? (zh ? "AI建议回复（可编辑后发送）" : "AI draft (editable)") : (zh ? "回复内容（可点 AI 拟稿）" : "Reply")}</p>
                  <textarea value={replyFor.text} onChange={(e) => setReplyFor({ ...replyFor, text: e.target.value })} rows={3}
                    className="w-full text-[10px] rounded border border-indigo-100 px-1.5 py-1 bg-white resize-none" data-testid="ops-inbound-reply-text" />
                  <div className="flex gap-1.5">
                    <button data-testid={`ops-inbound-reply-gen-${r.id}`} disabled={genBusy}
                      onClick={async () => {
                        setGenBusy(true);
                        try { const { data } = await api.post(`/engine/inbound/${r.id}/suggest-reply`); setReplyFor({ id: r.id, text: data.reply }); }
                        catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI拟稿失败" : "Failed")); }
                        setGenBusy(false);
                      }}
                      className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-white border border-indigo-200 text-indigo-600 disabled:opacity-40">{genBusy ? (zh ? "AI拟稿中…" : "…") : (zh ? "AI重拟" : "AI draft")}</button>
                    <button data-testid={`ops-inbound-reply-send-${r.id}`} disabled={!replyFor.text.trim()}
                      onClick={async () => { if (await run(() => api.post(`/engine/inbound/${r.id}/send-reply`, { body: replyFor.text }))) setReplyFor(null); }}
                      className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">{zh ? "确认发送" : "Send"}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SignoffCard({ ops, zh }) {
  const signoffs = ops.signoffs || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-signoff-card">
      <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><PenLine className="h-4 w-4 text-violet-600" />{zh ? "业主签字确认" : "Owner sign-off"}</p>
      {signoffs.length === 0 && <p className="text-[11px] text-slate-300 text-center py-3">{zh ? "阶段完成后自动生成确认书, 业主在门户免登录手写签字, 签名自动归档" : "None yet"}</p>}
      <div className="space-y-1.5">
        {signoffs.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2 py-1.5" data-testid={`ops-signoff-${s.id}`}>
            <p className="text-[11px] font-semibold text-slate-600 truncate">{T(s.phaseName)}</p>
            {s.status === "signed"
              ? <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ {s.signedName} · {fmtMD(s.signedAt)}</span>
              : <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{zh ? "待业主签字" : "pending"}</span>}
          </div>
        ))}
      </div>
      {signoffs.some((s) => s.status === "pending") && <p className="text-[10px] text-slate-400 mt-2">{zh ? "签字入口在业主门户（页头「业主门户」按钮生成链接），通知草稿已在通信中心" : ""}</p>}
    </div>
  );
}

function InspectionsCard({ ops, api, id, zh, run, pickFile, fileBusy }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ key: "", date: "", time: "" });
  const [failFor, setFailFor] = useState(null);
  const [fail, setFail] = useState({ riskNote: "", rectifyOwner: "", rectifyPlan: "" });
  const [reschedFor, setReschedFor] = useState(null);
  const [reschedDate, setReschedDate] = useState("");
  const submit = async () => {
    if (!form.key || !form.date) { toast.error(zh ? "请选择类型并填写日期" : "Type & date required"); return; }
    if (await run(() => api.post(`/projects/${id}/engine/inspections`, form), zh ? "市检已预约 · 分包通知草稿已生成" : "Scheduled")) {
      setAdding(false); setForm({ key: "", date: "", time: "" });
    }
  };
  const submitFail = async (iid) => {
    if (!fail.riskNote.trim()) { toast.error(zh ? "请填写问题描述" : "Risk note required"); return; }
    if (await run(() => api.post(`/projects/${id}/engine/inspections/${iid}/result`, { passed: false, ...fail }), zh ? "已登记未通过 · 自动创建阻塞项" : "Recorded")) {
      setFailFor(null); setFail({ riskNote: "", rectifyOwner: "", rectifyPlan: "" });
    }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-insp-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-red-500" />{zh ? "市检流程" : "Inspections"}</p>
        <Button data-testid="ops-insp-schedule-btn" size="sm" variant="outline" onClick={() => setAdding(!adding)} className="h-7 text-[11px]"><CalendarClock className="h-3 w-3 mr-1" />{zh ? "预约市检" : "Schedule"}</Button>
      </div>
      {ops.cityInfo && (
        <p className="text-[10px] text-slate-400 mb-2" data-testid="ops-insp-city-info">
          {zh ? "预约渠道" : "Channel"}: {ops.cityInfo.city}{ops.cityInfo.phone ? ` · ${ops.cityInfo.phone}` : ""}
          {ops.cityInfo.portal && <a href={ops.cityInfo.portal} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline ml-1">portal</a>}
          {ops.cityInfo.note ? ` · ${T(ops.cityInfo.note)}` : ""}
        </p>
      )}
      {adding && (
        <div className="mb-2 rounded-lg bg-slate-50 p-2 space-y-1.5" data-testid="ops-insp-form">
          <select value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full text-[11px] rounded border border-slate-200 px-1.5 py-1 bg-white" data-testid="ops-insp-type">
            <option value="">{zh ? "选择检查类型…" : "Type…"}</option>
            {(ops.inspectionTypes || []).map((t) => <option key={t.key} value={t.key}>{T(t.zh)}</option>)}
          </select>
          <div className="flex gap-1.5">
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="flex-1 text-[11px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-insp-date" />
            <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder={zh ? "时间(可选)" : "Time"} className="w-24 text-[11px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-insp-time" />
            <Button size="sm" onClick={submit} className="h-6 text-[11px]" data-testid="ops-insp-submit">{zh ? "预约" : "Add"}</Button>
          </div>
          <p className="text-[10px] text-slate-400">{zh ? "预约后关联任务转为「等待外部」，并生成分包SMS通知草稿" : ""}</p>
        </div>
      )}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {(ops.inspections || []).length === 0 && !adding && <p className="text-xs text-slate-300 text-center py-6">{zh ? "暂无市检记录 — 按知识库10步流程: 预约→检查→通过传Job Card / 未通过走整改闭环" : "No inspections"}</p>}
        {(ops.inspections || []).map((n) => (
          <div key={n.id} className="rounded-lg border border-slate-100 p-2" data-testid={`ops-insp-${n.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{T(n.zh)}</p>
              <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${INSP_CHIP[n.status] || ""}`}>{zh ? (ops.inspStatusZh[n.status] || n.status) : (INSP_EN[n.status] || n.status)}{n.failCount > 0 ? (zh ? ` · 第${n.failCount + 1}次` : ` · #${n.failCount + 1}`) : ""}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">{fmtD(n.scheduledDate)} {n.scheduledTime || ""}{n.riskNote ? ` · ${zh ? "问题" : "Issue"}: ${n.riskNote.slice(0, 40)}` : ""}</p>
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {n.status === "scheduled" && (
                <>
                  <a data-testid={`ops-insp-ics-${n.id}`} href={`${BACKEND}/api/public/engine/inspection/${n.id}.ics`}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 hover:bg-slate-200"><CalendarPlus className="h-2.5 w-2.5 inline mr-0.5" />{zh ? "加日历" : "ICS"}</a>
                  <button data-testid={`ops-insp-pass-${n.id}`} disabled={fileBusy} onClick={() => pickFile("pass", n.id)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700"><FileCheck2 className="h-2.5 w-2.5 inline mr-0.5" />{zh ? "通过·传Job Card" : "Pass"}</button>
                  <button data-testid={`ops-insp-fail-${n.id}`} onClick={() => setFailFor(failFor === n.id ? null : n.id)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-red-100 text-red-700 hover:bg-red-200">{zh ? "未通过" : "Fail"}</button>
                </>
              )}
              {n.status === "rectifying" && <button data-testid={`ops-insp-rectify-${n.id}`} disabled={fileBusy} onClick={() => pickFile("rectify", n.id)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-500 text-white hover:bg-amber-600"><Upload className="h-2.5 w-2.5 inline mr-0.5" />{zh ? "传整改证据" : "Evidence"}</button>}
              {n.status === "rectified" && (
                reschedFor === n.id ? (
                  <span className="flex gap-1 items-center">
                    <input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="text-[10px] rounded border border-slate-200 px-1 py-0.5" data-testid={`ops-insp-resched-date-${n.id}`} />
                    <button data-testid={`ops-insp-resched-submit-${n.id}`} onClick={async () => { if (await run(() => api.post(`/projects/${id}/engine/inspections/${n.id}/reschedule`, { date: reschedDate }), zh ? "已重新预约" : "Rescheduled")) setReschedFor(null); }} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-600 text-white">{zh ? "确定" : "OK"}</button>
                  </span>
                ) : (
                  <button data-testid={`ops-insp-resched-${n.id}`} onClick={() => { setReschedFor(n.id); setReschedDate(""); }} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200">{zh ? "重新预约" : "Reschedule"}</button>
                )
              )}
              {n.status === "passed" && <span className="text-[10px] text-emerald-600">✓ {zh ? "Job card 已归档" : "Job card archived"}{n.passedAt ? ` · ${String(n.passedAt).slice(5, 10)}` : ""}</span>}
            </div>
            {failFor === n.id && (
              <div className="mt-1.5 rounded bg-red-50 p-1.5 space-y-1" data-testid="ops-insp-fail-form">
                <input value={fail.riskNote} onChange={(e) => setFail({ ...fail, riskNote: e.target.value })} placeholder={zh ? "① 问题描述(必填)" : "Risk note"} className="w-full text-[10px] rounded border border-red-200 px-1.5 py-1" data-testid="ops-insp-fail-risk" />
                <input value={fail.rectifyOwner} onChange={(e) => setFail({ ...fail, rectifyOwner: e.target.value })} placeholder={zh ? "② 责任/整改人" : "Owner"} className="w-full text-[10px] rounded border border-red-200 px-1.5 py-1" data-testid="ops-insp-fail-owner" />
                <input value={fail.rectifyPlan} onChange={(e) => setFail({ ...fail, rectifyPlan: e.target.value })} placeholder={zh ? "③ 整改计划" : "Plan"} className="w-full text-[10px] rounded border border-red-200 px-1.5 py-1" data-testid="ops-insp-fail-plan" />
                <button onClick={() => submitFail(n.id)} className="w-full text-[10px] font-semibold rounded px-1.5 py-1 bg-red-600 text-white hover:bg-red-700" data-testid="ops-insp-fail-submit">{zh ? "登记未通过（自动创建阻塞项+整改SMS草稿）" : "Record fail"}</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MaterialsCard({ ops, api, id, zh, run, pickFile, fileBusy, canManage, gcal, setGcal }) {
  const connectGcal = async () => {
    try { const { data } = await api.post("/gcal/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "Google 日历尚未配置" : "Google Calendar not configured")); }
  };
  const disconnectGcal = async () => {
    try { await api.post("/gcal/disconnect"); setGcal((g) => ({ ...g, connected: false, email: "" })); toast.success(zh ? "已断开 Google 日历" : "Disconnected"); } catch {}
  };
  const today = todayStr();
  const mats = ops.materials || [];
  const multiRef = useRef(null);
  const multiFor = useRef(null);
  const [multiBusy, setMultiBusy] = useState(false);
  const [showMeasure, setShowMeasure] = useState(false);
  const [ms, setMs] = useState({ showerDoorWidth: "", mirrorWidth: "", notes: "" });
  const [msBusy, setMsBusy] = useState(false);
  const [etaFor, setEtaFor] = useState(null);
  const [etaDate, setEtaDate] = useState("");
  const [etaNote, setEtaNote] = useState("");
  const [etaBusy, setEtaBusy] = useState(false);
  const submitEta = async (mid) => {
    setEtaBusy(true);
    const ok = await run(() => api.post(`/projects/${id}/engine/materials/${mid}/eta`, { date: etaDate, note: etaNote }),
      zh ? "供应商 ETA 已确认 ✓ 可下载日历文件，到货前一天自动提醒" : "ETA confirmed — calendar ready, T-1 reminder set");
    if (ok) setEtaFor(null);
    setEtaBusy(false);
  };
  const onMultiFiles = async (fl) => {
    const mid = multiFor.current;
    multiFor.current = null;
    if (!mid || !fl?.length) return;
    setMultiBusy(true);
    try {
      const files = [];
      for (const f of Array.from(fl).slice(0, 6)) files.push(await chunkUpload(api, f));
      await run(() => api.post(`/projects/${id}/engine/materials/${mid}/order`, { files }), zh ? `已下单, ${files.length} 项证据已留档 ✓` : "Ordered");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setMultiBusy(false);
  };
  const submitMeasure = async () => {
    setMsBusy(true);
    const ok = await run(() => api.post(`/projects/${id}/engine/materials/measurement`, ms),
      zh ? "测量已提交 — 已触发「淋浴门+LED镜」订单组" : "Measurement submitted");
    if (ok) { setMs({ showerDoorWidth: "", mirrorWidth: "", notes: "" }); setShowMeasure(false); }
    setMsBusy(false);
  };
  const groupBadge = (g) => g === "drywall_day2_materials" ? (zh ? "组·瓷砖+柜门" : "Group A") : (zh ? "组·测量触发" : "Group B");
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-mat-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Package className="h-4 w-4 text-amber-500" />{zh ? "材料采购" : "Materials"}<span className="text-[11px] font-normal text-slate-400">{mats.filter((m) => m.status === "pending").length}{zh ? " 项待下单" : ""}</span></p>
        {canManage && <Button data-testid="ops-mat-backfill" size="sm" variant="outline" className="h-7 text-[11px]"
          onClick={() => run(() => api.post(`/projects/${id}/engine/materials/backfill`), zh ? "已按当前阶段生成采购清单" : "Backfilled")}>{zh ? "生成本阶段清单" : "Backfill"}</Button>}
      </div>
      {canManage && gcal && (
        <p className="text-[10px] mb-1.5 flex items-center gap-1.5 flex-wrap" data-testid="ops-gcal-status">
          <CalendarPlus className="h-3 w-3 text-slate-400" />
          {!gcal.configured ? <span className="text-slate-400">{zh ? "Google 日历待配置（需 GOOGLE_CLIENT_ID/SECRET）" : "Google Calendar pending setup"}</span>
            : gcal.connected ? (
              <>
                <span className="text-emerald-600 font-semibold">{zh ? "Google 日历已连接" : "Google Calendar connected"}{gcal.email ? ` · ${gcal.email}` : ""}</span>
                <button data-testid="ops-gcal-disconnect" onClick={disconnectGcal} className="text-slate-400 hover:text-red-500 underline">{zh ? "断开" : "Disconnect"}</button>
              </>
            ) : (
              <button data-testid="ops-gcal-connect" onClick={connectGcal} className="text-indigo-600 font-semibold hover:underline">{zh ? "连接 Google 日历（ETA 确认后自动推送日程）" : "Connect Google Calendar (auto-push confirmed ETAs)"}</button>
            )}
        </p>
      )}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {mats.length === 0 && <p className="text-xs text-slate-300 text-center py-6">{zh ? "暂无采购项 — 阶段推进时按知识库采购总表自动生成；也可点「生成本阶段清单」" : "No materials"}</p>}
        {mats.map((m) => {
          const overdue = m.status === "pending" && m.dueDate && String(m.dueDate) < today;
          const multi = (m.minEvidence || 0) > 1;
          return (
            <div key={m.id} className={`rounded-lg border p-2 ${overdue ? "border-red-200 bg-red-50/40" : "border-slate-100"}`} data-testid={`ops-mat-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{TB(m.label)}
                  {m.group && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-violet-100 text-violet-600" data-testid={`ops-mat-group-${m.id}`}>{groupBadge(m.group)}</span>}
                </p>
                <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${MAT_CHIP[m.status] || ""}`}>{zh ? (ops.matStatusZh[m.status] || m.status) : (MAT_EN[m.status] || m.status)}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">{zh ? m.orderTime : (META_EN[m.orderTime] || m.orderTime)} · {zh ? m.receive : (META_EN[m.receive] || m.receive)}{m.dueDate ? ` · ${zh ? "限" : "due"} ${String(m.dueDate).slice(5)}` : ""}{overdue && <span className="text-red-600 font-bold"> · {zh ? "已逾期" : "overdue"}</span>}</p>
              {m.evidence && m.status === "pending" && <p className="text-[10px] text-amber-600 mt-0.5">{zh ? "需证据" : "Evidence"}{multi ? `×${m.minEvidence}` : ""}: {T(m.evidence)}</p>}
              {m.emailKind && m.status === "pending" && <p className="text-[10px] text-indigo-500 mt-0.5">{zh ? "下单邮件草稿已生成 → 通信中心确认发送" : "Email draft in comm center"}</p>}
              {m.eta && (
                <p className="text-[10px] text-sky-600 mt-0.5" data-testid={`ops-mat-eta-${m.id}`}>🚚 ETA {m.eta}{m.etaNote ? ` · ${m.etaNote}` : ""} ·{" "}
                  <a href={`${BACKEND}/api/public/engine/material-eta/${m.id}.ics`} className="underline font-semibold hover:text-sky-800" data-testid={`ops-mat-eta-ics-${m.id}`}>{zh ? "📅 加入日历" : "📅 Add to calendar"}</a>
                  {m.gcalEventId ? (
                    <a href={m.gcalLink || "#"} target="_blank" rel="noreferrer" className="ml-1.5 text-emerald-600 font-semibold hover:underline" data-testid={`ops-mat-gcal-done-${m.id}`}>✓ Google</a>
                  ) : canManage && gcal?.connected ? (
                    <button data-testid={`ops-mat-gcal-push-${m.id}`} onClick={() => run(() => api.post(`/projects/${id}/engine/materials/${m.id}/gcal-push`), zh ? "已推送到您的 Google 日历 ✓" : "Pushed to Google Calendar ✓")}
                      className="ml-1.5 text-indigo-600 font-semibold hover:underline">{zh ? "推送 Google 日历" : "Push to Google"}</button>
                  ) : null}</p>
              )}
              <div className="flex gap-1.5 mt-1">
                {m.status === "pending" && (
                  <>
                    <button data-testid={`ops-mat-order-${m.id}`} disabled={fileBusy || multiBusy}
                      onClick={() => multi ? (multiFor.current = m.id, multiRef.current?.click()) : m.evidence ? pickFile("material", m.id) : run(() => api.post(`/projects/${id}/engine/materials/${m.id}/order`, {}), zh ? "已标记下单" : "Ordered")}
                      className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700">{multi ? (zh ? `已下单·传${m.minEvidence}证据` : "Order+2 proofs") : m.evidence ? (zh ? "已下单·传证据" : "Order+evidence") : (zh ? "已下单" : "Ordered")}</button>
                    {m.optional && <button data-testid={`ops-mat-skip-${m.id}`} onClick={() => run(() => api.post(`/projects/${id}/engine/materials/${m.id}/skip`, {}), zh ? "已跳过" : "Skipped")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "跳过" : "Skip"}</button>}
                  </>
                )}
                {m.status === "ordered" && <button data-testid={`ops-mat-received-${m.id}`} onClick={() => run(() => api.post(`/projects/${id}/engine/materials/${m.id}/received`), zh ? "已到货 ✓" : "Received")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-blue-600 text-white hover:bg-blue-700">{zh ? "已到货" : "Received"}</button>}
                {canManage && !["skipped", "received"].includes(m.status) && (
                  <button data-testid={`ops-mat-eta-btn-${m.id}`} onClick={() => { setEtaFor(etaFor === m.id ? null : m.id); setEtaDate(m.eta || ""); setEtaNote(m.etaNote || ""); }}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-sky-50 text-sky-600 hover:bg-sky-100">{m.eta ? (zh ? "改ETA" : "Edit ETA") : (zh ? "确认ETA" : "Confirm ETA")}</button>
                )}
                {m.status === "received" && m.receivedBy && <span className="text-[10px] text-emerald-600">✓ {m.receivedBy}</span>}
              </div>
              {etaFor === m.id && (
                <div className="flex gap-1.5 mt-1.5" data-testid={`ops-mat-eta-form-${m.id}`}>
                  <input type="date" value={etaDate} onChange={(e) => setEtaDate(e.target.value)} className="text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid={`ops-mat-eta-date-${m.id}`} />
                  <input value={etaNote} onChange={(e) => setEtaNote(e.target.value)} placeholder={zh ? "供应商回复摘要(可选)" : "Supplier reply note"} className="flex-1 min-w-0 text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid={`ops-mat-eta-note-${m.id}`} />
                  <button disabled={etaBusy || !etaDate} onClick={() => submitEta(m.id)} className="text-[10px] font-bold rounded px-2 bg-sky-600 text-white disabled:opacity-40" data-testid={`ops-mat-eta-save-${m.id}`}>{zh ? "确认" : "OK"}</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canManage && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <button data-testid="ops-mat-measure-toggle" onClick={() => setShowMeasure(!showMeasure)}
            className="text-[11px] font-semibold text-violet-600 hover:text-violet-800 flex items-center gap-1"><Ruler className="h-3.5 w-3.5" />{zh ? "提交现场测量（触发淋浴门+LED镜订单组）" : "Submit measurement"}</button>
          {showMeasure && (
            <div className="mt-2 space-y-1.5">
              <input value={ms.showerDoorWidth} onChange={(e) => setMs({ ...ms, showerDoorWidth: e.target.value })} placeholder={zh ? "淋浴门开口宽度, 如 60 in" : "Shower door width"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-measure-shower" />
              <input value={ms.mirrorWidth} onChange={(e) => setMs({ ...ms, mirrorWidth: e.target.value })} placeholder={zh ? "镜子宽度, 如 36 in" : "Mirror width"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-measure-mirror" />
              <input value={ms.notes} onChange={(e) => setMs({ ...ms, notes: e.target.value })} placeholder={zh ? "备注(可选)" : "Notes"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-measure-notes" />
              <Button size="sm" className="h-7 text-[11px] w-full" disabled={msBusy || (!ms.showerDoorWidth && !ms.mirrorWidth)} onClick={submitMeasure} data-testid="ops-measure-submit">{msBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "提交测量并触发订单组" : "Submit")}</Button>
            </div>
          )}
        </div>
      )}
      <input ref={multiRef} type="file" multiple className="hidden" data-testid="ops-mat-multi-input"
        onChange={(e) => { if (e.target.files?.length) onMultiFiles(Array.from(e.target.files)); e.target.value = ""; }} />
    </div>
  );
}

function CommsCard({ ops, api, id, zh, run, canManage, userRole, highlight }) {
  const [showSettings, setShowSettings] = useState(false);
  const [cfg, setCfg] = useState({ sms: ops.testRecipients?.sms || "", email: ops.testRecipients?.email || "" });
  const [editC, setEditC] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ channel: "sms", to: "", subject: "", body: "" });
  // 全局真实发送 / 通信设置：历来比同面板其他按钮更严（仅 admin/CEO/工程经理），
  // 故用独立动作点 /board::ops-broadcast，管理员可单独收掉「真实发送」而不影响日常操作。
  const { permOf: permOfComms } = useApp();
  const isAdmin = permOfComms("/board::ops-broadcast") ?? ["admin", "ceo", "eng_manager"].includes(userRole);
  const isHl = (c) => !!highlight && ((highlight.id && c.id === highlight.id) || (!highlight.id && highlight.rule && (c.rule || "").startsWith(highlight.rule)));
  useEffect(() => {
    if (!highlight) return;
    document.getElementById("ops-comm-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);
  const saveEdit = async () => {
    if (await run(() => api.patch(`/projects/${id}/engine/comms/${editC.id}`, { to: editC.to, subject: editC.subject, body: editC.body }), zh ? "草稿已保存" : "Saved")) setEditC(null);
  };
  const createDraft = async () => {
    if (!nc.body.trim()) { toast.error(zh ? "内容必填" : "Body required"); return; }
    if (await run(() => api.post(`/projects/${id}/engine/comms`, nc), zh ? "草稿已创建" : "Created")) { setAdding(false); setNc({ channel: "sms", to: "", subject: "", body: "" }); }
  };
  return (
    <div id="ops-comm-card" className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-comm-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><MessageSquareText className="h-4 w-4 text-indigo-500" />{zh ? "通信中心" : "Comms"}
          <span data-testid="ops-comm-testmode-badge" className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${ops.testMode ? "bg-blue-100 text-blue-700" : "bg-red-600 text-white"}`}>{ops.testMode ? (zh ? "测试模式" : "TEST") : (zh ? "真实发送" : "LIVE")}</span>
        </p>
        <span className="flex items-center gap-1.5">
          <button data-testid="ops-comm-new-btn" onClick={() => setAdding(!adding)} className="text-indigo-600 hover:text-indigo-800"><Plus className="h-4 w-4" /></button>
          {canManage && <button data-testid="ops-comm-settings-btn" onClick={() => setShowSettings(!showSettings)} className="text-slate-400 hover:text-slate-600"><Settings2 className="h-4 w-4" /></button>}
        </span>
      </div>
      {showSettings && (
        <div className="mb-2 rounded-lg bg-slate-50 p-2 space-y-1.5" data-testid="ops-comm-settings">
          <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid="ops-comm-testmode-toggle">
            <input type="checkbox" checked={!!ops.testMode} onChange={(e) => run(() => api.post(`/projects/${id}/engine/comm-test-mode`, { on: e.target.checked }), e.target.checked ? (zh ? "已开启测试模式" : "Test on") : (zh ? "⚠️ 已切换为真实发送" : "LIVE"))} className="accent-blue-600" />
            <span className="font-semibold text-slate-600">{zh ? "测试模式（短信/邮件转发测试接收人或仅留痕, 不联系真实业主/分包）" : "Test mode"}</span>
          </label>
          {isAdmin && (
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid="ops-comm-livedefault-toggle">
              <input type="checkbox" checked={!!ops.liveDefault} onChange={(e) => run(() => api.post(`/engine/settings/comm`, { liveDefault: e.target.checked }), e.target.checked ? (zh ? "🔴 全局默认真实发送已开启（10DLC 已注册项目将直发业主/供应商）" : "Global LIVE default ON") : (zh ? "全局默认已回到测试模式" : "Global default back to TEST"))} className="accent-red-600" />
              <span className="font-semibold text-red-600">{zh ? "全局默认真实发送（10DLC 已注册 · 未单独设置的项目直接真发）" : "Global LIVE default (10DLC registered)"}</span>
            </label>
          )}
          {isAdmin && (
            <div className="flex gap-1.5 items-center flex-wrap">
              <input value={cfg.sms} onChange={(e) => setCfg({ ...cfg, sms: e.target.value })} placeholder={zh ? "测试手机号 +1..." : "Test phone"} className="flex-1 min-w-[110px] text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-comm-test-sms" />
              <input value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder={zh ? "测试邮箱" : "Test email"} className="flex-1 min-w-[110px] text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-comm-test-email" />
              <button onClick={() => run(() => api.post(`/engine/settings/comm`, { testSmsTo: cfg.sms, testEmailTo: cfg.email }), zh ? "测试接收人已保存" : "Saved")} className="text-[10px] font-semibold rounded px-2 py-1 bg-slate-800 text-white" data-testid="ops-comm-settings-save">{zh ? "保存" : "Save"}</button>
            </div>
          )}
        </div>
      )}
      {adding && (
        <div className="mb-2 rounded-lg bg-slate-50 p-2 space-y-1.5" data-testid="ops-comm-new-form">
          <div className="flex gap-1.5">
            <select value={nc.channel} onChange={(e) => setNc({ ...nc, channel: e.target.value })} className="text-[10px] rounded border border-slate-200 px-1.5 py-1 bg-white" data-testid="ops-comm-new-channel">
              <option value="sms">SMS</option><option value="email">Email</option>
            </select>
            <input value={nc.to} onChange={(e) => setNc({ ...nc, to: e.target.value })} placeholder={nc.channel === "sms" ? (zh ? "手机号" : "Phone") : (zh ? "邮箱" : "Email")} className="flex-1 text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-comm-new-to" />
          </div>
          {nc.channel === "email" && <input value={nc.subject} onChange={(e) => setNc({ ...nc, subject: e.target.value })} placeholder="Subject" className="w-full text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-comm-new-subject" />}
          <textarea value={nc.body} onChange={(e) => setNc({ ...nc, body: e.target.value })} rows={2} placeholder={zh ? "消息内容…" : "Body…"} className="w-full text-[10px] rounded border border-slate-200 px-1.5 py-1" data-testid="ops-comm-new-body" />
          <button onClick={createDraft} className="w-full text-[10px] font-semibold rounded px-1.5 py-1 bg-indigo-600 text-white" data-testid="ops-comm-new-submit">{zh ? "创建草稿" : "Create draft"}</button>
        </div>
      )}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {(ops.comms || []).length === 0 && !adding && <p className="text-xs text-slate-300 text-center py-6">{zh ? "暂无通信记录 — 引擎在关键节点自动生成草稿（开工通知/市检联动/下单邮件），确认后发送" : "No comms"}</p>}
        {(ops.comms || []).map((c) => (
          <div key={c.id} className={`rounded-lg border p-2 ${isHl(c) ? "border-amber-400 ring-2 ring-amber-300 bg-amber-50/60" : "border-slate-100"}`} data-testid={`ops-comm-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold text-slate-500 flex items-center gap-1 min-w-0">
                {c.channel === "sms" ? <MessageSquareText className="h-3 w-3 text-emerald-500 shrink-0" /> : <Mail className="h-3 w-3 text-blue-500 shrink-0" />}
                <span className="truncate">{c.subject || (c.toName || c.toRole || "")}{c.to ? ` · ${c.to}` : ""}</span>
              </p>
              <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${COMM_CHIP[c.status] || ""}`}>{zh ? (ops.commStatusZh[c.status] || c.status) : (COMM_EN[c.status] || c.status)}</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{T(c.body)}</p>
            <p className="text-[9px] text-slate-300 mt-0.5">{String(c.createdAt || "").slice(5, 16).replace("T", " ")}{c.sentTo ? ` · ${zh ? "发至" : "to"} ${c.sentTo}` : ""}{c.sentBy ? ` · ${c.sentBy}` : ""}</p>
            {["draft", "failed", "logged"].includes(c.status) && (
              <div className="flex gap-1.5 mt-1">
                <button data-testid={`ops-comm-send-${c.id}`} onClick={() => run(() => api.post(`/projects/${id}/engine/comms/${c.id}/send`), null)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-600 text-white hover:bg-indigo-700"><Send className="h-2.5 w-2.5 inline mr-0.5" />{zh ? "发送" : "Send"}</button>
                <button data-testid={`ops-comm-edit-${c.id}`} onClick={() => setEditC({ ...c })} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 hover:bg-slate-200"><Pencil className="h-2.5 w-2.5 inline mr-0.5" />{zh ? "编辑" : "Edit"}</button>
                <button data-testid={`ops-comm-discard-${c.id}`} onClick={() => run(() => api.post(`/projects/${id}/engine/comms/${c.id}/discard`), zh ? "已弃用" : "Discarded")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-50 text-slate-400 hover:bg-slate-100"><Trash2 className="h-2.5 w-2.5 inline" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {editC && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditC(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="ops-comm-edit-modal">
            <p className="font-bold text-slate-800 text-sm mb-3">{zh ? "编辑通信草稿" : "Edit draft"} · {editC.channel.toUpperCase()}</p>
            {(ops.vendors || []).length > 0 && (
              <select data-testid="ops-comm-edit-vendor" defaultValue="" onChange={(e) => {
                const v = (ops.vendors || []).find((x) => x.id === e.target.value);
                if (v) setEditC({ ...editC, to: editC.channel === "sms" ? (v.phone || "") : (v.email || ""), toName: v.name });
              }} className="w-full text-[11px] rounded-lg border border-indigo-200 bg-indigo-50/50 px-2 py-1.5 mb-2">
                <option value="">{zh ? "📒 从供应商台账带出联系方式…" : "Pick from vendor directory…"}</option>
                {(ops.vendors || []).map((v) => <option key={v.id} value={v.id}>{`${T(v.name)}（${v.trade || "-"}${editC.channel === "sms" ? (v.phone ? ` · ${v.phone}` : (zh ? " · 无电话" : " · no phone")) : (v.email ? ` · ${v.email}` : (zh ? " · 无邮箱" : " · no email"))}）`}</option>)}
              </select>
            )}
            <label className="text-[11px] text-slate-400">{editC.channel === "sms" ? (zh ? "收件手机号" : "Phone") : (zh ? "收件邮箱" : "Email")}</label>
            <input value={editC.to || ""} onChange={(e) => setEditC({ ...editC, to: e.target.value })} className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 mb-2" data-testid="ops-comm-edit-to" />
            {editC.channel === "email" && (
              <>
                <label className="text-[11px] text-slate-400">Subject</label>
                <input value={editC.subject || ""} onChange={(e) => setEditC({ ...editC, subject: e.target.value })} className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 mb-2" data-testid="ops-comm-edit-subject" />
              </>
            )}
            <label className="text-[11px] text-slate-400">{zh ? "内容" : "Body"}</label>
            <textarea value={editC.body || ""} onChange={(e) => setEditC({ ...editC, body: e.target.value })} rows={7} className="w-full text-xs rounded-lg border border-slate-200 px-2 py-1.5 mb-3" data-testid="ops-comm-edit-body" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditC(null)}>{zh ? "取消" : "Cancel"}</Button>
              <Button size="sm" className="h-7 text-xs" onClick={saveEdit} data-testid="ops-comm-edit-save">{zh ? "保存草稿" : "Save"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PUNCH_CHIP = {
  sent: ["已发整改", "Sent", "bg-amber-100 text-amber-700"], submitted: ["待审核", "In review", "bg-blue-100 text-blue-700"],
  closed: ["已关闭", "Closed", "bg-emerald-100 text-emerald-700"], open: ["待发送", "Open", "bg-slate-100 text-slate-500"],
};

function PunchCard({ api, id, zh, canManage, refreshTick, onChanged }) {
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ title: "", desc: "", assignName: "", assignPhone: "" });
  const [photos, setPhotos] = useState([]);
  const photoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/punch`); setD(data); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/engine/punch`, { ...f, photoIds: photos.map((p) => p.fileId) });
      toast.success(zh ? "整改项已创建 · 分包链接短信草稿已生成 → 通信中心确认发送" : "Punch created", { duration: 6000 });
      if (data.url) { try { await navigator.clipboard.writeText(data.url); toast.info(zh ? "整改链接已复制到剪贴板" : "Link copied"); } catch {} }
      setF({ title: "", desc: "", assignName: "", assignPhone: "" });
      setPhotos([]);
      setShow(false);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const review = async (p, approve) => {
    let note = "";
    if (!approve) {
      note = window.prompt(zh ? "驳回原因（会同步显示在分包链接页）:" : "Reject reason:") || "";
      if (!note) return;
    }
    try {
      await api.post(`/engine/punch/${p.id}/review`, { approve, note });
      toast.success(approve ? (zh ? "整改已验收关闭 ✓" : "Closed") : (zh ? "已驳回 · 原因已同步至分包链接页" : "Rejected"));
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const items = d?.items || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-punch-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4 text-red-500" />{zh ? "Punch List 整改" : "Punch List"}
          {d?.openCount > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600" data-testid="ops-punch-open-count">{d.openCount} {zh ? "项未关闭" : "open"}</span>}
          {d?.allClosed && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="ops-punch-all-closed">✓ {zh ? "全部关闭" : "All closed"}</span>}
        </p>
        {canManage && <Button data-testid="ops-punch-new" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShow(!show)}><Plus className="h-3 w-3 mr-0.5" />{zh ? "录整改" : "New"}</Button>}
      </div>
      {d?.openCount > 0 && <p className="text-[10px] text-red-500 bg-red-50 rounded-lg px-2 py-1 mb-2">{zh ? "⛔ 存在未关闭整改项（全局阻塞）— 全部整改经 PM 审核关闭后方可交付" : "Open punch items block delivery"}</p>}
      {show && (
        <div className="space-y-1.5 mb-2.5 rounded-lg border border-slate-100 p-2">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={zh ? "整改标题, 如: 主卧墙面油漆补刷" : "Title"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-punch-title" />
          <input value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} placeholder={zh ? "详细说明(可选, 用于自动判断工种)" : "Description"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-punch-desc" />
          <div className="flex gap-1.5">
            <input value={f.assignName} onChange={(e) => setF({ ...f, assignName: e.target.value })} placeholder={zh ? "分包姓名*" : "Sub name*"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-punch-assign" />
            <input value={f.assignPhone} onChange={(e) => setF({ ...f, assignPhone: e.target.value })} placeholder={zh ? "电话(发链接短信)" : "Phone"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-punch-phone" />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="ops-punch-photo-input" onChange={async (e) => { const fl = Array.from(e.target.files || []); e.target.value = ""; if (!fl.length) return; setBusy(true); try { for (const file of fl) { const up = await chunkUpload(api, file); setPhotos((ps) => [...ps, { fileId: up.fileId || up.id, filename: file.name }]); } } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); } setBusy(false); }} />
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} onClick={() => photoRef.current?.click()} data-testid="ops-punch-photo-btn"><Upload className="h-3 w-3 mr-0.5" />{zh ? "整改前照片" : "Before photos"}{photos.length ? ` (${photos.length})` : ""}</Button>
            {photos.map((ph, i) => <span key={ph.fileId} className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`ops-punch-photo-chip-${i}`}>{ph.filename}<button onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))} className="text-slate-400">×</button></span>)}
          </div>
          <Button size="sm" className="h-7 text-[11px] w-full" disabled={busy || !f.title || !f.assignName} onClick={create} data-testid="ops-punch-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "创建整改 + 生成分包链接短信草稿" : "Create")}</Button>
        </div>
      )}
      <div className="space-y-1.5 max-h-[28rem] overflow-y-auto">
        {items.length === 0 && <p className="text-xs text-slate-300 text-center py-5">{zh ? "暂无整改项" : "No punch items"}</p>}
        {items.map((p) => {
          const [lab, labEn, cls] = PUNCH_CHIP[p.status] || PUNCH_CHIP.open;
          return (
            <div key={p.id} className="rounded-lg border border-slate-100 p-2" data-testid={`ops-punch-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{T(p.title)}</p>
                <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{zh ? lab : labEn}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">{p.trade} · {p.assignName}{(p.before || []).length + (p.after || []).length > 0 ? ` · ${zh ? "前" : "before"} ${(p.before || []).length} / ${zh ? "后" : "after"} ${(p.after || []).length}` : ""}</p>
              {p.submitNote && p.status === "submitted" && <p className="text-[10px] text-blue-600 mt-0.5">“{p.submitNote}”</p>}
              {p.reviewNote && p.status !== "closed" && <p className="text-[10px] text-red-500 mt-0.5">{zh ? "上次驳回" : "Rejected"}: {p.reviewNote}</p>}
              <PunchCompare punch={p} zh={zh} />
              {canManage && p.status === "submitted" && (
                <div className="flex gap-1.5 mt-1.5">
                  <button data-testid={`ops-punch-approve-${p.id}`} onClick={() => review(p, true)} className="text-[10px] font-semibold rounded px-2 py-1 bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "验收通过·关闭" : "Approve"}</button>
                  <button data-testid={`ops-punch-reject-${p.id}`} onClick={() => review(p, false)} className="text-[10px] font-semibold rounded px-2 py-1 bg-red-100 text-red-600 hover:bg-red-200">{zh ? "驳回" : "Reject"}</button>
                </div>
              )}
              {p.status === "closed" && <p className="text-[10px] text-emerald-600 mt-0.5">✓ {p.reviewedBy} · {fmtD(p.reviewedAt)}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskLinksCard({ api, id, zh, canManage, refreshTick }) {
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ taskId: "", subName: "", subPhone: "" });
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/task-links`); setD(data); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/engine/task-links`, f);
      toast.success(zh ? (data.smsDraftId ? T("链接已创建 · 短信草稿已生成 → 通信中心确认发送") : T("链接已创建")) : "Created", { duration: 6000 });
      if (data.url) { try { await navigator.clipboard.writeText(data.url); toast.info(zh ? "任务链接已复制到剪贴板" : "Link copied"); } catch {} }
      setF({ taskId: "", subName: "", subPhone: "" });
      setShow(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const openThread = async (lk) => {
    if (thread?.lid === lk.id) { setThread(null); return; }
    try {
      const { data } = await api.get(`/engine/task-links/${lk.id}/thread`);
      setThread({ lid: lk.id, msgs: data.msgs || [] });
      load();
    } catch {}
  };
  const sendReply = async () => {
    if (!reply.trim() || !thread) return;
    setReplyBusy(true);
    try {
      const { data } = await api.post(`/engine/task-links/${thread.lid}/reply`, { text: reply });
      setThread((t) => ({ ...t, msgs: [...t.msgs, data.msg] }));
      setReply("");
      toast.success(zh ? "已回复 · 分包链接页可见" : "Replied");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setReplyBusy(false);
  };
  const copyUrl = async (lk) => { try { await navigator.clipboard.writeText(lk.url); toast.success(zh ? "链接已复制" : "Copied"); } catch {} };
  const revoke = async (lk) => {
    try { await api.post(`/engine/task-links/${lk.id}/revoke`); toast.success(zh ? "链接已撤销" : "Revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const links = d?.links || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-tasklink-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Link2 className="h-4 w-4 text-indigo-500" />{zh ? "分包任务链接" : "Task Links"}
          <span className="text-[11px] font-normal text-slate-400">{links.filter((l) => l.status === "active").length}</span></p>
        {canManage && (
          <div className="flex gap-1.5">
            <button data-testid="ops-weekly-sub-run" onClick={async () => {
              try {
                const { data } = await api.post("/engine/weekly-sub-remind", { projectId: id, force: true });
                toast.success(zh ? `周催办预演（${data.week}）：${data.subs} 家分包 ${data.drafts} 条草稿已生成 → 通信中心确认` : `${data.drafts} drafts`, { duration: 6000 });
              } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
            }} className="text-[10px] font-semibold rounded px-1.5 py-1 bg-amber-50 text-amber-600 hover:bg-amber-100">{zh ? "周催办预演" : "Weekly remind"}</button>
            <button data-testid="ops-midcheck-run" onClick={async () => {
              try {
                const { data } = await api.post("/engine/mid-progress-check", { projectId: id, force: true });
                toast.success(zh ? `中途进度检查：已向 ${data.checked} 个连续工序链接发出催照提醒（短信草稿→通信中心确认）` : `Mid-progress check sent to ${data.checked} link(s)`, { duration: 6000 });
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
            }} className="text-[10px] font-semibold rounded px-1.5 py-1 bg-sky-50 text-sky-600 hover:bg-sky-100">{zh ? "中途检查" : "Mid-check"}</button>
            <Button data-testid="ops-tasklink-new" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShow(!show)}><Plus className="h-3 w-3 mr-0.5" />{zh ? "发链接" : "New"}</Button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-400 mb-2">{zh ? "分包免登录提交照片/进度/留言, 站内实时同步提醒, 可在此直接回复；每周一自动为未完成分包生成催办短信草稿" : "Sub self-service link with two-way sync + weekly auto reminders"}</p>
      {show && (
        <div className="space-y-1.5 mb-2.5 rounded-lg border border-slate-100 p-2">
          <select value={f.taskId} onChange={(e) => setF({ ...f, taskId: e.target.value })} className="w-full text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="ops-tasklink-task">
            <option value="">{zh ? "选择当前阶段任务…" : "Select task…"}</option>
            {(d?.tasks || []).map((t) => <option key={t.id} value={t.id}>{t.title.slice(0, 40)}</option>)}
          </select>
          <div className="flex gap-1.5">
            <input value={f.subName} onChange={(e) => setF({ ...f, subName: e.target.value })} placeholder={zh ? "分包姓名*" : "Sub name*"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-tasklink-sub" />
            <input value={f.subPhone} onChange={(e) => setF({ ...f, subPhone: e.target.value })} placeholder={zh ? "电话(发短信)" : "Phone"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-tasklink-phone" />
          </div>
          <Button size="sm" className="h-7 text-[11px] w-full" disabled={busy || !f.taskId || !f.subName} onClick={create} data-testid="ops-tasklink-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "生成链接 + 短信草稿" : "Create")}</Button>
        </div>
      )}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {links.length === 0 && <p className="text-xs text-slate-300 text-center py-5">{zh ? "暂无任务链接" : "No links"}</p>}
        {links.map((lk) => (
          <div key={lk.id} className={`rounded-lg border p-2 ${lk.status === "revoked" ? "border-slate-100 opacity-50" : "border-slate-100"}`} data-testid={`ops-tasklink-${lk.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{T(lk.title)}
                {lk.unreadForPm > 0 && <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid={`ops-tasklink-unread-${lk.id}`}>{lk.unreadForPm}</span>}
              </p>
              <span className="shrink-0 text-[9px] text-slate-400">{lk.status === "revoked" ? (zh ? "已撤销" : "revoked") : lk.subName}</span>
            </div>
            {lk.scheduleDate && <p className="text-[10px] text-indigo-600 mt-0.5">📅 {zh ? "分包选择时间" : "Scheduled"}: {fmtD(lk.scheduleDate)}</p>}
            {lk.midCheckAt && <p className="text-[10px] text-sky-600 mt-0.5" data-testid={`ops-tasklink-midcheck-${lk.id}`}>📸 {zh ? "已发中途进度检查" : "Mid-check sent"} · {String(lk.midCheckAt).slice(5, 10)}</p>}
            {lk.lastMsg && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{lk.lastMsg.from === "sub" ? "📨" : "↩"} {lk.lastMsg.text || (zh ? "(附件)" : "(file)")}</p>}
            <div className="flex gap-1.5 mt-1">
              <button data-testid={`ops-tasklink-thread-${lk.id}`} onClick={() => openThread(lk)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100">{thread?.lid === lk.id ? (zh ? "收起" : "Hide") : (zh ? "留言/回复" : "Thread")}</button>
              <button data-testid={`ops-tasklink-copy-${lk.id}`} onClick={() => copyUrl(lk)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200 inline-flex items-center gap-0.5"><Copy className="h-2.5 w-2.5" />{zh ? "复制链接" : "Copy"}</button>
              {canManage && lk.status === "active" && <button data-testid={`ops-tasklink-revoke-${lk.id}`} onClick={() => revoke(lk)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-red-50 text-red-500 hover:bg-red-100">{zh ? "撤销" : "Revoke"}</button>}
            </div>
            {thread?.lid === lk.id && (
              <div className="mt-1.5 border-t border-slate-100 pt-1.5">
                <div className="space-y-1 max-h-40 overflow-y-auto" data-testid={`ops-tasklink-msgs-${lk.id}`}>
                  {thread.msgs.length === 0 && <p className="text-[10px] text-slate-300 text-center py-2">{zh ? "暂无留言" : "No messages"}</p>}
                  {thread.msgs.map((m) => (
                    <p key={m.id} className={`text-[10px] ${m.from === "sub" ? "text-slate-600" : "text-indigo-600"}`}>
                      <b>{m.authorName}</b>: {T(m.text)}{(m.files || []).length > 0 ? ` 📎×${m.files.length}` : ""}</p>
                  ))}
                </div>
                {canManage && (
                  <div className="flex gap-1.5 mt-1.5">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={zh ? "回复分包(链接页可见)…" : "Reply…"}
                      onKeyDown={(e) => { if (e.key === "Enter") sendReply(); }}
                      className="flex-1 min-w-0 text-[11px] rounded-lg border border-slate-200 px-2 py-1" data-testid={`ops-tasklink-reply-${lk.id}`} />
                    <button disabled={replyBusy || !reply.trim()} onClick={sendReply} className="rounded-lg px-2 bg-slate-900 text-white disabled:opacity-40" data-testid={`ops-tasklink-reply-send-${lk.id}`}><Send className="h-3 w-3" /></button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const openConfirmPdf = async (api, pid, cid, lang) => {
  try {
    const res = await api.get(`/projects/${pid}/engine/owner-confirmations/${cid}.pdf`, { params: { lang }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
};

function OwnerConfirmCard({ api, id, zh, canManage, refreshTick }) {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/owner-confirmations`); setD(data); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/engine/owner-confirmations`, { templateKey: pick });
      toast.success(zh ? "确认书已发布到业主门户 · 业主短信草稿已生成 → 通信中心确认发送" : "Created", { duration: 6000 });
      setPick("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const items = d?.items || [];
  const avail = (d?.templates || []).filter((t) => !t.used);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-confirm-card">
      <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><PenLine className="h-4 w-4 text-indigo-500" />{zh ? "业主阶段确认书" : "Owner Confirmations"}
        <span className="text-[11px] font-normal text-slate-400">{items.filter((x) => x.status === "signed").length}/{items.length}</span></p>
      {canManage && (
        <div className="flex gap-1.5 mb-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white" data-testid="ops-confirm-template">
            <option value="">{zh ? "选择阶段模板（9类·双语）…" : "Select template…"}</option>
            {avail.map((t) => <option key={t.key} value={t.key}>{zh ? `${T(t.titleZh)}（${T(t.stage)}·${t.itemCount}项）` : `${t.titleEn || t.titleZh} (${T(t.stage)} · ${t.itemCount} items)`}</option>)}
          </select>
          <Button size="sm" className="h-8 text-[11px] shrink-0" disabled={busy || !pick} onClick={create} data-testid="ops-confirm-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "发布" : "Send")}</Button>
        </div>
      )}
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {items.length === 0 && <p className="text-xs text-slate-300 text-center py-4">{zh ? "暂无确认书 — 按阶段节点发布给业主签署" : "No confirmations"}</p>}
        {items.map((c) => (
          <div key={c.id} className="rounded-lg border border-slate-100 p-2" data-testid={`ops-confirm-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{zh ? c.titleZh : (c.titleEn || T(c.titleZh))}</p>
              <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${c.status === "signed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.status === "signed" ? (zh ? "已签署" : "Signed") : (zh ? "待签署" : "Pending")}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-1.5">{T(c.stage)} · {(c.items || []).length} {zh ? "项确认" : "items"}{c.status === "signed" ? ` · ✍️ ${c.signedName} ${fmtD(c.signedAt)}` : ""}
              <span className="inline-flex items-center gap-1 ml-auto" data-testid={`ops-confirm-pdf-${c.id}`}>PDF{["zh", "en", "es"].map((l) => <button key={l} type="button" onClick={() => openConfirmPdf(api, id, c.id, l)} className="px-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 uppercase" data-testid={`ops-confirm-pdf-${c.id}-${l}`}>{l}</button>)}</span></p>
          </div>
        ))}
      </div>
    </div>
  );
}

const LAYOUT_CHIP = {
  vendor_review: ["供应商审核中", "Vendor review", "bg-blue-100 text-blue-700"], owner_review: ["待业主确认", "Awaiting owner", "bg-amber-100 text-amber-700"],
  approved: ["业主已确认", "Approved", "bg-emerald-100 text-emerald-700"], changes_requested: ["要求修改", "Changes requested", "bg-red-100 text-red-600"],
};

function LayoutCard({ api, id, zh, canManage, refreshTick }) {
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ vendorName: "", vendorPhone: "", note: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/layouts`); setD(data); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const onFiles = async (fl) => {
    setUpBusy(true);
    try {
      const added = [];
      for (const file of Array.from(fl).slice(0, 4)) added.push(await chunkUpload(api, file));
      setFiles((p) => [...p, ...added]);
    } catch { toast.error(zh ? "上传失败" : "Upload failed"); }
    setUpBusy(false);
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/engine/layouts`, { ...f, files });
      toast.success(zh ? "布局审核已发起 · 供应商链接短信草稿已生成 → 通信中心确认发送" : "Created", { duration: 6000 });
      if (data.vendorUrl) { try { await navigator.clipboard.writeText(data.vendorUrl); toast.info(zh ? "供应商审核链接已复制" : "Link copied"); } catch {} }
      setF({ vendorName: "", vendorPhone: "", note: "" });
      setFiles([]);
      setShow(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const copy = async (url, label) => { if (!url) return; try { await navigator.clipboard.writeText(url); toast.success(label + (zh ? "已复制" : " copied")); } catch {} };
  const items = d?.items || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-layout-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><LayoutTemplate className="h-4 w-4 text-violet-500" />{zh ? "橱柜布局审核" : "Cabinet Layout"}</p>
        {canManage && <Button data-testid="ops-layout-new" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShow(!show)}><Plus className="h-3 w-3 mr-0.5" />{zh ? "发起审核" : "New"}</Button>}
      </div>
      <p className="text-[10px] text-slate-400 mb-2">{zh ? "供应商链接审核布局图 → 批准后自动生成业主最终确认链接" : "Vendor review → auto owner confirmation"}</p>
      {show && (
        <div className="space-y-1.5 mb-2.5 rounded-lg border border-slate-100 p-2">
          <label className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-200 py-2 cursor-pointer text-slate-400 hover:text-violet-600 hover:border-violet-300" data-testid="ops-layout-upload-label">
            {upBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            <span className="text-[11px] font-semibold">{zh ? "上传布局图 (图片/PDF)" : "Upload layout"}</span>
            <input type="file" multiple accept="image/*,.pdf" className="hidden" data-testid="ops-layout-file-input"
              onChange={(e) => { if (e.target.files?.length) onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
          </label>
          {files.length > 0 && <p className="text-[10px] text-slate-500" data-testid="ops-layout-files">📄 {files.map((x) => x.filename).join("、")}</p>}
          <div className="flex gap-1.5">
            <input value={f.vendorName} onChange={(e) => setF({ ...f, vendorName: e.target.value })} placeholder={zh ? "供应商姓名*" : "Vendor*"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-layout-vendor" />
            <input value={f.vendorPhone} onChange={(e) => setF({ ...f, vendorPhone: e.target.value })} placeholder={zh ? "电话(发短信)" : "Phone"} className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-layout-phone" />
          </div>
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={zh ? "备注(可选)" : "Note"} className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="ops-layout-note" />
          <Button size="sm" className="h-7 text-[11px] w-full" disabled={busy || upBusy || files.length === 0 || !f.vendorName} onClick={create} data-testid="ops-layout-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "发起供应商审核 + 短信草稿" : "Create")}</Button>
        </div>
      )}
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {items.length === 0 && <p className="text-xs text-slate-300 text-center py-4">{zh ? "暂无布局审核" : "No layouts"}</p>}
        {items.map((l) => {
          const [lab, labEn, cls] = LAYOUT_CHIP[l.status] || ["-", "-", ""];
          return (
            <div key={l.id} className="rounded-lg border border-slate-100 p-2" data-testid={`ops-layout-${l.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{(l.files || []).length} {zh ? "张图" : "files"} · {l.vendorName}</p>
                <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cls}`} data-testid={`ops-layout-status-${l.id}`}>{zh ? lab : labEn}</span>
              </div>
              {l.vendorNote && <p className="text-[10px] text-amber-600 mt-0.5">{zh ? "供应商" : "Vendor"}: {l.vendorNote}</p>}
              {l.ownerNote && <p className="text-[10px] text-amber-600 mt-0.5">{zh ? "业主" : "Owner"}: {l.ownerNote}</p>}
              <div className="flex gap-1.5 mt-1">
                {l.vendorUrl && <button data-testid={`ops-layout-copyv-${l.id}`} onClick={() => copy(l.vendorUrl, zh ? "供应商链接" : "Vendor link ")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "供应商链接" : "Vendor"}</button>}
                {l.ownerUrl && <button data-testid={`ops-layout-copyo-${l.id}`} onClick={() => copy(l.ownerUrl, zh ? "业主确认链接" : "Owner link ")} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100">{zh ? "业主链接" : "Owner"}</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PRECON_STATUS = {
  draft: ["备料中", "Preparing", "bg-slate-100 text-slate-500"],
  published: ["待业主签署", "Awaiting owner", "bg-amber-100 text-amber-700"],
  signed: ["已签署", "Signed", "bg-emerald-100 text-emerald-700"],
};

function PreConCard({ api, id, zh, canManage, refreshTick }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upFor, setUpFor] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/precon`); setD(data); } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const onFiles = async (key, fl) => {
    setUpFor(key);
    try {
      const files = [];
      for (const f of Array.from(fl).slice(0, 5)) files.push(await chunkUpload(api, f));
      await api.post(`/projects/${id}/engine/precon/upload`, { key, files });
      toast.success(zh ? "文件已上传 ✓" : "Uploaded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setUpFor(null);
  };
  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/engine/precon/publish`);
      toast.success(zh ? "Pre-Con 三件套已发布到业主门户 · 业主短信草稿已生成 → 通信中心确认发送" : "Published to owner portal", { duration: 6000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };
  const [stz, ste, stc] = PRECON_STATUS[d?.status] || PRECON_STATUS.draft;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-precon-card">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><FileCheck2 className="h-4 w-4 text-sky-600" />{zh ? "Pre-Con 三件套" : "Pre-Con Package"}</p>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${stc}`} data-testid="ops-precon-status">{zh ? stz : ste}</span>
      </div>
      <p className="text-[10px] text-slate-400 mb-2">{zh ? "场地图 + 变更单 + 开工文件三份齐全后一键发布，业主门户一次打包签署" : "Site plan + change order + pre-con docs, one bundled owner signature"}</p>
      <div className="space-y-1.5">
        {(d?.items || []).map((it) => (
          <div key={it.key} className={`rounded-lg border p-2 ${it.files.length ? "border-emerald-100 bg-emerald-50/40" : "border-slate-100"}`} data-testid={`ops-precon-${it.key}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-slate-700 min-w-0 truncate">{it.files.length ? "✓ " : ""}{TB(it.label)}</p>
              {canManage && d?.status !== "signed" && (
                <label className="shrink-0 text-[10px] font-semibold rounded px-1.5 py-0.5 bg-sky-50 text-sky-600 hover:bg-sky-100 cursor-pointer" data-testid={`ops-precon-upload-${it.key}`}>
                  {upFor === it.key ? <Loader2 className="h-3 w-3 animate-spin inline" /> : (it.files.length ? (zh ? "重传" : "Replace") : (zh ? "上传" : "Upload"))}
                  <input type="file" multiple accept="image/*,.pdf" className="hidden" data-testid={`ops-precon-input-${it.key}`}
                    onChange={(e) => { if (e.target.files?.length) onFiles(it.key, Array.from(e.target.files)); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {it.files.length > 0 && <p className="text-[10px] text-slate-400 mt-0.5 truncate">📄 {it.files.map((f) => f.filename).join("、")}</p>}
          </div>
        ))}
      </div>
      {d?.status === "signed" ? (
        <p className="text-[11px] font-semibold text-emerald-600 mt-2" data-testid="ops-precon-signed">✍️ {d.signedName} {zh ? "已打包签署" : "signed"} · {fmtD(d.signedAt)} · {zh ? "签名凭证已归档" : "archived"}</p>
      ) : d?.status === "published" ? (
        <p className="text-[10px] text-amber-600 mt-2" data-testid="ops-precon-waiting">{zh ? "已发布到业主门户，等待业主打包签署（业主短信草稿见通信中心）" : "Published — awaiting owner signature"}</p>
      ) : (
        canManage && <Button size="sm" className="h-7 text-[11px] w-full mt-2" disabled={busy || !d?.ready} onClick={publish} data-testid="ops-precon-publish">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (d?.ready ? (zh ? "发布到业主门户打包签署" : "Publish for bundled signature") : (zh ? "三份文件齐全后可发布" : "Upload all 3 docs first"))}</Button>
      )}
    </div>
  );
}

function CloseoutCard({ api, id, zh, canManage, refreshTick }) {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState({});
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/engine/closeout`);
      setD(data);
      setNotes(Object.fromEntries((data.items || []).map((it) => [it.key, it.note || ""])));
    } catch {}
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const setItem = async (it, done) => {
    try {
      const { data } = await api.post(`/projects/${id}/engine/closeout/${it.key}`, { done, note: notes[it.key] || "" });
      if (data.allDone) toast.success(zh ? "🏁 四项齐全 — 项目交付闭环完成！" : "Closeout complete!", { duration: 6000 });
      else if (!done && it.done) toast.info(zh ? "已撤回该项 — 交付状态回退为未完成" : "Item reverted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };
  const items = d?.items || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ops-closeout-card">
      <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-1"><Flag className="h-4 w-4 text-emerald-600" />{zh ? "交付闭环 Closeout" : "Closeout"}
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d?.completedAt ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="ops-closeout-progress">{d ? `${d.doneCount}/4` : "-"}</span>
        {d?.completedAt && <span className="text-[10px] font-bold text-emerald-600" data-testid="ops-closeout-done">✓ {zh ? "已交付" : "Delivered"}</span>}
      </p>
      <p className="text-[10px] text-slate-400 mb-2">{zh ? "City Final 通过 ≠ 交付完成 — 四项齐全才算正式交付" : "City Final ≠ delivery. All 4 required."}</p>
      {d?.certFileId && (
        <a data-testid="ops-closeout-cert" href={`${BACKEND}/api/files/${d.certFileId}/download?auth=${authStore.get("ali_token") || ""}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 mb-2 text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-lg px-2 py-1 hover:bg-emerald-100">📜 {zh ? "下载交付证书 PDF（业主门户同步可见）" : "Delivery certificate PDF (also on owner portal)"}</a>
      )}
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.key} className={`rounded-lg border p-2 ${it.done ? "border-emerald-100 bg-emerald-50/40" : "border-slate-100"}`} data-testid={`ops-closeout-${it.key}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!it.done} disabled={!canManage} onChange={(e) => setItem(it, e.target.checked)}
                className="h-3.5 w-3.5 accent-emerald-600" data-testid={`ops-closeout-check-${it.key}`} />
              <span className={`text-[11px] font-bold ${it.done ? "text-emerald-700" : "text-slate-700"}`}>{TB(it.label)}</span>
            </label>
            {it.done && it.by && <p className="text-[10px] text-slate-400 mt-0.5 pl-5">✓ {it.by} · {fmtD(it.at)}{it.note ? ` · ${T(it.note)}` : ""}</p>}
            {!it.done && canManage && (
              <input value={notes[it.key] || ""} onChange={(e) => setNotes({ ...notes, [it.key]: e.target.value })}
                placeholder={zh ? "备注(勾选时保存)" : "Note"} className="w-full mt-1 text-[10px] rounded border border-slate-100 px-1.5 py-1" data-testid={`ops-closeout-note-${it.key}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
